import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiEdit2, FiWifi, FiActivity } from "react-icons/fi";
import { MdSettingsInputAntenna, MdWater, MdPower } from "react-icons/md";
import { GiWaterTank } from "react-icons/gi";
import { IotmateButton, DialogBox, InfoCard } from "../components/Shared";
import { DataHistoryTable } from "../components/Table";
import fetchDataFromApi from "../api/IotmateRestApi";
import { generateDummyWaterLevelDetails, generateDummyWaterLevelHistory } from "../Data/Data";
import "./WaterLevelDetails.css";

const WaterLevelDetails = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [historicalData, setHistoricalData] = useState([]);

  useEffect(() => {
    let isInitialLoad = true;

    const fetchData = async () => {
      try {
        if (isInitialLoad) {
          setLoading(true);
        }
        const data = await fetchDataFromApi(`water_level_details/${deviceId}`);
        setDeviceData(data || generateDummyWaterLevelDetails(deviceId));
      } catch (error) {
        console.error("Error fetching water level details:", error);
        setDeviceData(generateDummyWaterLevelDetails(deviceId));
      } finally {
        if (isInitialLoad) {
          setLoading(false);
          isInitialLoad = false;
        }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [deviceId]);

  useEffect(() => {
    const fetchHistoricalData = async () => {
      try {
        const data = await fetchDataFromApi(`water_level_history/${deviceId}`);
        setHistoricalData(data || generateDummyWaterLevelHistory(deviceId));
      } catch (error) {
        console.error("Error fetching historical data:", error);
        setHistoricalData(generateDummyWaterLevelHistory(deviceId));
      }
    };

    fetchHistoricalData();
  }, [deviceId]);

  const historyColumns = React.useMemo(
    () => [
      {
        Header: "Timestamp",
        accessor: "timestamp",
        Cell: ({ value }) => new Date(value).toLocaleString(),
      },
      {
        Header: "Water Level (%)",
        accessor: "waterLevel",
      },
      {
        Header: "Motor Status",
        accessor: "motorStatus",
      },
      {
        Header: "Lora Signal (dBm)",
        accessor: "loraSignal",
      },
      {
        Header: "Battery (%)",
        accessor: "battery",
      },
    ],
    []
  );

  const handleEditClick = () => {
    setNewName(deviceData?.name || "");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setNewName("");
  };

  const handleSaveName = () => {
    if (newName.trim()) {
      setDeviceData({ ...deviceData, name: newName.trim() });
      setIsDialogOpen(false);
      setNewName("");
    }
  };

  if (loading) {
    return (
      <div className="wl-details-loading">
        <div className="spinner"></div>
        <p>Loading device details...</p>
      </div>
    );
  }

  if (!deviceData) {
    return (
      <div className="wl-details-error">
        <p>Failed to load device data</p>
        <button onClick={() => navigate(-1)} className="wl-details-back-btn">
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="wl-details-container">
      {/* Header */}
      <div className="wl-details-header">
        <button onClick={() => navigate(-1)} className="wl-details-back-btn">
          <FiArrowLeft size={20} />
          Back
        </button>
        <div className="wl-details-title-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 className="wl-details-title">
              {deviceData.name || `Water Level Sensor ${deviceId}`}
            </h1>
            <IotmateButton onClick={handleEditClick} variant="default" className="iotmate-btn-icon">
              <FiEdit2 size={18} />
            </IotmateButton>
          </div>
          <div className="wl-details-status">
            {deviceData.status === "Online" ? (
              <FiCheckCircle size={24} color="#8fce00" />
            ) : (
              <FiXCircle size={24} color="#ff6b6b" />
            )}
            <span
              style={{
                color: deviceData.status === "Online" ? "#8fce00" : "#ff6b6b",
              }}
            >
              {deviceData.status || "Offline"}
            </span>
          </div>
        </div>
        <p className="wl-details-last-update">
          Last updated: {deviceData.lastUpdate || "N/A"}
        </p>
      </div>

      {/* Main Info Cards */}
      <div className="info-card-grid">
        {/* Water Level Card - Custom with progress bar */}
        <div className="wl-info-card wl-water-level">
          <div className="wl-card-icon">
            <GiWaterTank size={48} color="#0ea5e9" />
          </div>
          <div className="wl-card-content">
            <h3 className="wl-card-title">Water Level</h3>
            <div className="wl-card-value">{deviceData.waterLevel}%</div>
            <div className="wl-progress-bar">
              <div
                className="wl-progress-fill"
                style={{ width: `${deviceData.waterLevel}%`, background: '#0ea5e9' }}
              ></div>
            </div>
          </div>
        </div>

        {/* Motor Status Card */}
        <InfoCard
          icon={<MdPower />}
          iconColor={deviceData.motorStatus === "ON" ? "#10b981" : "#6b7280"}
          title="Motor Status"
          value={deviceData.motorStatus}
          subtitle={deviceData.motorStatus === "ON" ? "Pump Running" : "Pump Idle"}
        />

        {/* Lora Connectivity Card */}
        <InfoCard
          icon={<MdSettingsInputAntenna />}
          iconColor="#8b5cf6"
          title="Lora Connectivity"
          value={`${deviceData.loraSignal} dBm`}
          subtitle={`Signal Strength: ${deviceData.loraSignal > -100 ? "Good" : "Weak"}`}
        />

        {/* Gateway Info Card */}
        <InfoCard
          icon={<FiWifi />}
          iconColor="#f59e0b"
          title="Gateway"
          value={deviceData.gatewayName}
          subtitle={`ID: ${deviceData.gatewayId}`}
        />
      </div>

      {/* Additional Info */}
      <div className="wl-additional-info">
        <div className="wl-info-item">
          <FiActivity size={20} />
          <span className="wl-info-label">Battery:</span>
          <span className="wl-info-value">{deviceData.battery}%</span>
        </div>
        <div className="wl-info-item">
          <MdWater size={20} />
          <span className="wl-info-label">Tank Capacity:</span>
          <span className="wl-info-value">{deviceData.tankCapacity} L</span>
        </div>
      </div>

      {/* Historical Data Table */}
      <div className="wl-details-history">
        <DataHistoryTable
          data={historicalData}
          columns={historyColumns}
          title={`${deviceData.name || 'Device'} - Historical Data`}
          dateField="timestamp"
          enableDateFilter={true}
          enableDownload={true}
        />
      </div>

      {/* Edit Name Dialog */}
      <DialogBox
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        title="Edit Device Name"
        onSave={handleSaveName}
        saveLabel="Save"
        cancelLabel="Cancel"
      >
        <input
          type="text"
          className="dialog-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Enter device name"
          autoFocus
        />
      </DialogBox>
    </div>
  );
};

export default WaterLevelDetails;
